// src/features/products/components/ProductCard.tsx
import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Product } from '../types';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { ReactComponent as IconAddToCart } from '../../../assets/iconAddToCart.svg';
import { ReactComponent as IconPrev } from '../../../assets/prev.svg';
import { ReactComponent as IconNext } from '../../../assets/next.svg';

interface ProductCardProps {
  product: Product;
  onClick?: () => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product, onClick }) => {
  const [imageIndex, setImageIndex] = useState(0);
  const { currency } = useCurrency();
  const currencySymbol = currency === 'THB' ? '฿' : '€';
  const hasMultipleImages = product.images && product.images.length > 1;

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setImageIndex((prev) => (prev === 0 ? product.images.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setImageIndex((prev) => (prev === product.images.length - 1 ? 0 : prev + 1));
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        width: '100%',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
        }
      }}
    >
      {/* รูปภาพ */}
      <Box sx={{
        width: '100%',
        paddingTop: '100%',
        position: 'relative',
        overflow: 'hidden',
        '&:hover .nav-btn': {
          opacity: 1,
        },
      }}>
        {product.images && product.images.length > 0 ? (
          <img
            src={product.images[imageIndex].src}
            alt={product.name}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px'
          }}>
            No Image
          </Box>
        )}

        {/* Prev / Next Buttons */}
        {hasMultipleImages && (
          <>
            <Box
              className="nav-btn"
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: 0,
                transition: 'opacity 0.3s',
              }}
            >
              <IconPrev width={7} height={13} />
            </Box>
            <Box
              className="nav-btn"
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: 0,
                transition: 'opacity 0.3s',
              }}
            >
              <IconNext width={7} height={13} />
            </Box>
          </>
        )}

        {/* Sale Badge */}
        {product.on_sale && (
          <Box sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            bgcolor: '#f44336',
            color: 'white',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            fontSize: '12px',
            fontWeight: 600
          }}>
            SALE
          </Box>
        )}
      </Box>

      {/* ข้อมูลสินค้า */}
      <Box sx={{ p: 2 }}>
        {/* ชื่อสินค้า */}
        <Typography
          variant="h6"
          sx={{
            fontSize: '16px',
            fontFamily: 'Montserrat',
            fontWeight: 500,
            mb: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '48px'
          }}
        >
          {product.name}
        </Typography>

        {/* ราคา */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: '20px',
              fontWeight: 700,
              color: product.on_sale ? '#f44336' : '#000'
            }}
          >
            {currencySymbol}{(product.price?.[currency] ?? 0).toLocaleString()}
          </Typography>

          {/* ราคาเต็ม (ถ้าลดราคา) */}
          {product.on_sale && product.regular_price && (
            <Typography
              variant="body2"
              sx={{
                fontSize: '16px',
                color: '#999',
                textDecoration: 'line-through'
              }}
            >
              {currencySymbol}{(product.regular_price?.[currency] ?? 0).toLocaleString()}
            </Typography>
          )}

          {/* Add to cart icon */}
          <Box
            sx={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              bgcolor: 'white',
              borderRadius: 1,
              transition: 'all 0.3s',
              '& svg path': {
                stroke: 'black',
                transition: 'stroke 0.3s',
              },
              '&:hover': {
                bgcolor: 'black',
                '& svg path': {
                  stroke: 'white',
                },
              },
            }}
            onClick={(e) => { e.stopPropagation(); onClick?.(); }}
          >
            <IconAddToCart width={32} height={22} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};